// @greenlight-spec: 11-observability.md
import { z } from 'zod';
/**
 * Coarse upstream state. `unknown` is the fail-quiet value: a checker that
 * cannot reach its source says so rather than reporting an outage, because a
 * broken checker crying `major` is worse than no signal at all.
 */
export const UpstreamStatusStateSchema = z.enum(['operational', 'degraded', 'major', 'unknown']);
/**
 * Which signal produced the state. `vendor_status_page` is the vendor's own
 * claim and is labelled as such in the UI, since Greenlight never calls these
 * APIs on the user's behalf and has no independent view. `observed` is
 * Greenlight's own call-failure rate, which catches partial and Enterprise-tier
 * outages a status page lags on or never reports.
 *
 * Named `signal` rather than `source` on the wire: `source` is a forbidden
 * field name in the fleet-report allowlist (docs/11 §Report allowlist), which
 * matches on the name alone and cannot know this one means provenance.
 */
export const UpstreamStatusSignalSchema = z.enum(['vendor_status_page', 'observed']);
/**
 * Cap on relayed vendor text. Long enough for a real status-page summary, short
 * enough that it cannot dominate an error envelope or a banner.
 */
export const UPSTREAM_DESCRIPTION_MAX_LENGTH = 200;
/** Trims vendor free text to the cap, preserving null. Apply at every boundary. */
export function capUpstreamDescription(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return null;
    return trimmed.length > UPSTREAM_DESCRIPTION_MAX_LENGTH
        ? `${trimmed.slice(0, UPSTREAM_DESCRIPTION_MAX_LENGTH - 1).trimEnd()}…`
        : trimmed;
}
export const UpstreamStatusEntrySchema = z.object({
    /** Stable vendor key from the catalog, e.g. `github`. */
    key: z.string(),
    /** Display name, e.g. `GitHub`. */
    label: z.string(),
    state: UpstreamStatusStateSchema,
    signal: UpstreamStatusSignalSchema,
    /**
     * The vendor's own one-line summary, when it published one.
     *
     * Third-party free text, capped at the parser so every sink inherits the
     * bound: it reaches a builder agent's `next_steps`, and a multi-kilobyte
     * incident description would balloon every SCM error envelope for the length
     * of the incident. Sinks must attribute it to the vendor rather than let it
     * read as Greenlight's own words.
     */
    description: z.string().max(UPSTREAM_DESCRIPTION_MAX_LENGTH).nullable(),
    last_checked_at: z.string().datetime({ offset: true }),
});
export const UpstreamStatusSchema = z.array(UpstreamStatusEntrySchema);
/** States that warrant telling users their work may fail. */
export function isUpstreamImpaired(state) {
    return state === 'degraded' || state === 'major';
}
/**
 * SCM error codes that mean the caller got it wrong, not that the upstream is
 * unwell. Two places need the same answer and must not drift: the observed
 * failure-rate window, where counting these would let one developer's typos
 * look like an outage, and the agent-facing error note, where telling someone
 * to wait out an incident would steer them away from fixing a genuinely bad
 * call.
 *
 * This list is a fast exclusion, not the verdict. `scm.upstream_error` is the
 * default arm of the provider's status switch — every 4xx that is not 401/403/404 (or
 * 409 where the provider distinguishes a stale-write conflict) lands there, as do seven hand-thrown local-validation failures with no network
 * call at all — so the code alone cannot say who is at fault.
 * {@link blamesScmUpstream} decides on the evidence instead.
 */
export const SCM_CALLER_FAULT_CODES = new Set([
    'scm.not_found',
    'scm.permission_denied',
    'scm.conflict',
    'scm.branch_behind',
    'scm.repo_exists',
    'scm.manifest_invalid',
    'scm.manifest_state_invalid',
    'scm.workflow_mismatch',
    'scm.repo_too_large',
    'scm.artifact_too_large',
    'scm.webhook_signature_invalid',
    'scm.webhook_duplicate_delivery',
]);
/** Node's `cause` chain for a fetch that never got an answer. */
const NETWORK_FAULT_PATTERN = /ECONNRESET|ECONNREFUSED|ETIMEDOUT|EAI_AGAIN|ENOTFOUND|EPIPE|ABORT_ERR|socket hang up|network|aborted/i;
function mentionsNetworkFault(value, depth = 0) {
    if (depth > 4 || value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return NETWORK_FAULT_PATTERN.test(value);
    if (typeof value !== 'object')
        return false;
    const err = value;
    return (mentionsNetworkFault(err.code, depth + 1) ||
        mentionsNetworkFault(err.name, depth + 1) ||
        mentionsNetworkFault(err.message, depth + 1) ||
        mentionsNetworkFault(err.cause, depth + 1));
}
/**
 * Whether a failure is evidence the SCM itself is unwell.
 *
 * Blame is read off what the failure carries, never off the code string: the
 * provider's mapper stamps `details.status` from the HTTP response, so a 5xx is
 * the upstream's fault and any 4xx is the caller's — a 422 from `commitFiles`, a
 * 409 head-SHA mismatch from `mergePullRequest`, a 429 — all routine outcomes of
 * agent traffic that share one code with genuine outages. A failure carrying no
 * status counts only when its cause names a transport fault, which leaves the
 * hand-thrown local-validation throws (a malformed `owner/repo`, a non-numeric
 * run id, a plan that will not do private-repo branch protection) out of the
 * window where they belong.
 *
 * Unrecognised throwables are the caller's problem by default: a `TypeError`
 * from our own mapping code is a Greenlight defect, and counting it would make a
 * crash inside a provider method indistinguishable from GitHub being down.
 */
export function blamesScmUpstream(error) {
    if (error === null || typeof error !== 'object')
        return false;
    const code = error.code;
    if (typeof code !== 'string' || !code.startsWith('scm.'))
        return false;
    if (SCM_CALLER_FAULT_CODES.has(code))
        return false;
    const details = error.details;
    const status = details !== null && typeof details === 'object'
        ? details.status
        : undefined;
    if (typeof status === 'number')
        return status >= 500;
    return mentionsNetworkFault(error);
}
//# sourceMappingURL=upstream-status.js.map